import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";

import {
  parseLabelScanElementString,
  resolveGs1ManufacturerPartNumber,
  type LabelScanFields,
} from "@integrator-src/lib/gs1-element-string";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { Button } from "@/components/ui/Button";
import { Input, Label, TextArea } from "@/components/ui/Input";
import { SearchableSelect, type SearchableSelectOption } from "@/components/ui/SearchableSelect";
import { FLOOR_TYPING_INPUT_PROPS } from "@/lib/floorSerialScan";
import { useFloorPageScanCapture } from "@/lib/floorPageScanCapture";
import { useFloorTypingInputHandlers } from "@/lib/useFloorTypingInputKeyboard";
import { skuPickerDescriptionLine, skuPickerTitleLine } from "@/lib/mrpSkuPickerDisplay";

import type { InboundLineRow } from "./inboundApi";
import { lookupInboundAmlByMpn, searchInboundSkus } from "./inboundApi";

function ExceptionModalShell(props: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}): JSX.Element | null {
  if (!props.open) return null;
  return createPortal(
    <div
      {...hubModalOverlayProps(true)}
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) props.onClose();
      }}
    >
      <div
        className="mt-2 w-full max-w-md rounded-lg border border-slate-200 bg-white p-4 shadow-xl"
        onPointerDown={(event) => event.stopPropagation()}
      >
        <h2 className="text-base font-semibold text-slate-900">{props.title}</h2>
        <div className="mt-3 space-y-3">{props.children}</div>
      </div>
    </div>,
    document.body,
  );
}

export function InboundReceiveDamagedModal(props: {
  open: boolean;
  line: InboundLineRow | null;
  currentGoodQuantity: number;
  pending: boolean;
  onClose: () => void;
  onSubmit: (input: { goodQuantity: number; damagedQuantity: number; notes: string }) => void;
}): JSX.Element | null {
  const [goodQuantity, setGoodQuantity] = useState("");
  const [damagedQuantity, setDamagedQuantity] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!props.open) return;
    setGoodQuantity(String(props.currentGoodQuantity));
    setDamagedQuantity("");
    setNotes("");
  }, [props.open, props.currentGoodQuantity, props.line?.source_item_id]);

  return (
    <ExceptionModalShell open={props.open} title="Flag damaged quantity" onClose={props.onClose}>
      {props.line != null ? (
        <>
          <p className="text-sm text-slate-600">
            Line <span className="font-semibold text-slate-900">{props.line.sku}</span>. Good quantity stays on the
            receipt; damaged units go to Holding for inspection.
          </p>
          <div>
            <Label htmlFor="inbound-damaged-good-qty">Good quantity</Label>
            <Input
              id="inbound-damaged-good-qty"
              inputMode="numeric"
              value={goodQuantity}
              onChange={(event) => setGoodQuantity(event.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="inbound-damaged-qty">Damaged quantity</Label>
            <Input
              id="inbound-damaged-qty"
              inputMode="numeric"
              value={damagedQuantity}
              onChange={(event) => setDamagedQuantity(event.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="inbound-damaged-notes">Notes (optional)</Label>
            <TextArea
              id="inbound-damaged-notes"
              rows={2}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="secondary" onClick={props.onClose}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              disabled={props.pending}
              onClick={() => {
                const good = Number.parseFloat(goodQuantity.trim());
                const damaged = Number.parseFloat(damagedQuantity.trim());
                if (!Number.isFinite(good) || good < 0 || !Number.isFinite(damaged) || damaged <= 0) return;
                props.onSubmit({ goodQuantity: good, damagedQuantity: damaged, notes: notes.trim() });
              }}
            >
              {props.pending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
              Save damaged split
            </Button>
          </div>
        </>
      ) : null}
    </ExceptionModalShell>
  );
}

export function InboundReceiveUnlistedSkuModal(props: {
  open: boolean;
  initialSku: string;
  pending: boolean;
  onClose: () => void;
  onSubmit: (input: { sku: string }) => void;
}): JSX.Element | null {
  const [sku, setSku] = useState(props.initialSku);
  const [skuSearch, setSkuSearch] = useState(props.initialSku);

  useEffect(() => {
    if (!props.open) return;
    setSku(props.initialSku);
    setSkuSearch(props.initialSku);
  }, [props.open, props.initialSku]);

  // Fetch as soon as the modal opens (empty query returns a starter list) so the
  // dropdown is never stuck on "type more characters" with zero options.
  const skuSearchQuery = useQuery({
    queryKey: ["shipping", "inbound", "sku-search", skuSearch.trim()],
    queryFn: () => searchInboundSkus(skuSearch.trim()),
    enabled: props.open,
    staleTime: 30_000,
  });

  const skuOptions = useMemo((): SearchableSelectOption[] => {
    return (skuSearchQuery.data ?? []).map((row) => {
      const descriptionLine = skuPickerDescriptionLine(row);
      return {
        value: row.sku,
        label: skuPickerTitleLine(row),
        description: descriptionLine.length > 0 ? descriptionLine : null,
      };
    });
  }, [skuSearchQuery.data]);

  const selectedSkuOption = useMemo((): SearchableSelectOption | null => {
    const trimmed = sku.trim();
    if (trimmed === "") return null;
    const match = skuOptions.find((option) => option.value === trimmed);
    return match ?? { value: trimmed, label: trimmed, description: null };
  }, [sku, skuOptions]);

  return (
    <ExceptionModalShell open={props.open} title="SKU not on order" onClose={props.onClose}>
      <p className="text-sm text-slate-600">
        Adds a dock line for a catalog SKU that is not on this PO or transfer. Count quantity on the numpad after
        adding.
      </p>
      <div>
        <Label htmlFor="inbound-unlisted-sku">SKU</Label>
        <SearchableSelect
          id="inbound-unlisted-sku"
          presentation="inlineFilter"
          autoFocus
          value={sku}
          onValueChange={(option) => {
            setSku(option?.value ?? "");
            if (option != null) setSkuSearch(option.value);
          }}
          options={skuOptions}
          searchValue={skuSearch}
          onSearchChange={(query) => {
            setSkuSearch(query);
            if (query.trim() === "") setSku("");
          }}
          placeholder="Search catalog SKU"
          searchPlaceholder="Search catalog SKU"
          emptyMessage={skuSearchQuery.isFetching ? "Searching…" : "No matching SKUs"}
          isLoading={skuSearchQuery.isFetching}
          selectedLabelOverride={selectedSkuOption?.label ?? null}
        />
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" variant="secondary" onClick={props.onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          disabled={props.pending || sku.trim() === ""}
          onClick={() => {
            const trimmedSku = sku.trim();
            if (trimmedSku === "") return;
            props.onSubmit({ sku: trimmedSku });
          }}
        >
          {props.pending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
          Add line
        </Button>
      </div>
    </ExceptionModalShell>
  );
}

export function InboundReceiveNonSkuModal(props: {
  open: boolean;
  pending: boolean;
  onClose: () => void;
  onSubmit: (input: { description: string; quantity: number }) => void;
}): JSX.Element | null {
  const [description, setDescription] = useState("");
  const [quantity, setQuantity] = useState("");

  useEffect(() => {
    if (!props.open) return;
    setDescription("");
    setQuantity("");
  }, [props.open]);

  return (
    <ExceptionModalShell open={props.open} title="Non-SKU item" onClose={props.onClose}>
      <p className="text-sm text-slate-600">
        For parts without a catalog SKU. Adds a description-only line and opens a quality dispute for review.
      </p>
      <div>
        <Label htmlFor="inbound-non-sku-description">Description</Label>
        <TextArea
          id="inbound-non-sku-description"
          rows={2}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
      </div>
      <div>
        <Label htmlFor="inbound-non-sku-qty">Quantity received</Label>
        <Input
          id="inbound-non-sku-qty"
          inputMode="numeric"
          value={quantity}
          onChange={(event) => setQuantity(event.target.value)}
        />
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" variant="secondary" onClick={props.onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          disabled={props.pending}
          onClick={() => {
            const parsed = Number.parseFloat(quantity.trim());
            if (description.trim() === "" || !Number.isFinite(parsed) || parsed <= 0) return;
            props.onSubmit({ description: description.trim(), quantity: parsed });
          }}
        >
          {props.pending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
          Add item
        </Button>
      </div>
    </ExceptionModalShell>
  );
}

export function InboundReceiveMpnMismatchModal(props: {
  open: boolean;
  sku: string | null;
  scannedMpn: string;
  disabled?: boolean;
  onRetry: () => void;
}): JSX.Element | null {
  const trimmedMpn = props.scannedMpn.trim();
  return (
    <ExceptionModalShell open={props.open} title="Manufacturer part number" onClose={props.onRetry}>
      <p className="text-base font-medium text-rose-700">
        {trimmedMpn !== ""
          ? `"${trimmedMpn}" does not match ${props.sku ?? "this item"}.`
          : "This MPN does not match this item."}
      </p>
      <p className="text-base text-slate-600">
        Scan or type the manufacturer part number from the label. Counting stays blocked until it matches an approved part.
      </p>
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        <Button
          type="button"
          variant="secondary"
          className="min-h-12 flex-1 text-base touch-manipulation"
          disabled={props.disabled}
          onClick={props.onRetry}
        >
          Scan again
        </Button>
      </div>
    </ExceptionModalShell>
  );
}

export function InboundReceiveAmlLookupModal(props: {
  open: boolean;
  onClose: () => void;
  onSelectCpn: (cpn: string) => void;
}): JSX.Element | null {
  const inputRef = useRef<HTMLInputElement>(null);
  const [mpnDraft, setMpnDraft] = useState("");
  const [searchMpn, setSearchMpn] = useState("");
  const [labelFields, setLabelFields] = useState<LabelScanFields | null>(null);

  useEffect(() => {
    if (!props.open) return;
    setMpnDraft("");
    setSearchMpn("");
    setLabelFields(null);
  }, [props.open]);

  const typingInputHandlers = useFloorTypingInputHandlers();

  useFloorPageScanCapture({
    enabled: props.open,
    autoFocus: false,
    captureUnfocusedHid: false,
    onScan: (value) => {
      const trimmed = resolveGs1ManufacturerPartNumber(value);
      if (trimmed.length < 3) return;
      setMpnDraft(trimmed);
      setLabelFields(parseLabelScanElementString(value));
      setSearchMpn(trimmed);
    },
  });

  const lookupQuery = useQuery({
    queryKey: ["shipping", "inbound", "aml-lookup", searchMpn.trim()],
    queryFn: () => lookupInboundAmlByMpn(searchMpn),
    enabled: props.open && searchMpn.trim().length >= 3,
    retry: false,
  });

  const rows = lookupQuery.data ?? [];
  const lookupError =
    lookupQuery.error instanceof Error ? lookupQuery.error.message : lookupQuery.isError ? "Lookup failed." : null;

  const runLookup = (): void => {
    const trimmed = resolveGs1ManufacturerPartNumber(mpnDraft);
    if (trimmed.length < 3) return;
    setMpnDraft(trimmed);
    setSearchMpn(trimmed);
  };

  return (
    <ExceptionModalShell open={props.open} title="AML lookup" onClose={props.onClose}>
      <p className="text-sm text-slate-600">
        Enter or scan the manufacturer part number from the reel or bag label. GS1 data matrix labels with AI 240 are
        supported. Tap a matching CPN to highlight that line below.
      </p>
      <div>
        <Label htmlFor="inbound-aml-mpn">Manufacturer part number (MPN)</Label>
        <div className="mt-1 flex gap-2">
          <Input
            id="inbound-aml-mpn"
            ref={inputRef}
            value={mpnDraft}
            {...FLOOR_TYPING_INPUT_PROPS}
            enterKeyHint="search"
            {...typingInputHandlers}
            onChange={(event) => setMpnDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== "Enter") return;
              event.preventDefault();
              runLookup();
            }}
            placeholder="Full MPN from label"
          />
          <Button
            type="button"
            variant="primary"
            className="shrink-0"
            disabled={mpnDraft.trim().length < 3 || lookupQuery.isFetching}
            onClick={runLookup}
          >
            {lookupQuery.isFetching ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
            Find CPN
          </Button>
        </div>
        {labelFields != null && (labelFields.lot != null || labelFields.serial != null) ? (
          <p className="mt-1 text-xs text-slate-500">
            {labelFields.lot != null ? `Lot ${labelFields.lot}` : null}
            {labelFields.lot != null && labelFields.serial != null ? " · " : null}
            {labelFields.serial != null ? `Serial ${labelFields.serial}` : null}
          </p>
        ) : null}
      </div>
      <div className="max-h-[min(16rem,40vh)] space-y-2 overflow-y-auto rounded-md border border-slate-200 bg-slate-50 p-2">
        {searchMpn.trim().length < 3 ? (
          <p className="text-sm text-slate-500">Type at least 3 characters, then tap Find CPN.</p>
        ) : lookupQuery.isFetching ? (
          <p className="text-sm text-slate-500">Searching approved AML…</p>
        ) : lookupError != null ? (
          <p className="text-sm text-rose-700" role="alert">{lookupError}</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-slate-600">No approved AML match for this MPN.</p>
        ) : (
          rows.map((row) => (
            <button
              key={`${row.cpn}:${row.mpn}:${row.manufacturer_name ?? ""}`}
              type="button"
              className="flex w-full flex-col rounded-md border border-slate-200 bg-white px-3 py-2 text-left hover:border-brand-400 hover:bg-brand-50"
              onClick={() => {
                props.onSelectCpn(row.cpn);
                props.onClose();
              }}
            >
              <span className="font-semibold text-slate-900">{row.cpn}</span>
              <span className="text-xs text-slate-600">
                {row.manufacturer_name != null && row.manufacturer_name !== "" ? `${row.manufacturer_name} · ` : ""}
                {row.mpn}
              </span>
              {row.description != null && row.description !== "" ? (
                <span className="mt-0.5 text-xs text-slate-500 line-clamp-2">{row.description}</span>
              ) : null}
            </button>
          ))
        )}
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" variant="secondary" onClick={props.onClose}>
          Cancel
        </Button>
      </div>
    </ExceptionModalShell>
  );
}

export function InboundHoldingSkuModal(props: {
  open: boolean;
  currentSku: string;
  pending: boolean;
  onClose: () => void;
  onSubmit: (sku: string) => void;
}): JSX.Element | null {
  const [sku, setSku] = useState(props.currentSku);
  const [skuSearch, setSkuSearch] = useState("");

  useEffect(() => {
    if (!props.open) return;
    setSku(props.currentSku);
    setSkuSearch("");
  }, [props.open, props.currentSku]);

  const skuSearchQuery = useQuery({
    queryKey: ["shipping", "inbound", "sku-search", "holding", skuSearch.trim()],
    queryFn: () => searchInboundSkus(skuSearch.trim()),
    enabled: props.open,
    staleTime: 30_000,
  });

  const skuOptions = useMemo((): SearchableSelectOption[] => {
    return (skuSearchQuery.data ?? []).map((row) => {
      const descriptionLine = skuPickerDescriptionLine(row);
      return {
        value: row.sku,
        label: skuPickerTitleLine(row),
        description: descriptionLine.length > 0 ? descriptionLine : null,
      };
    });
  }, [skuSearchQuery.data]);

  const selectedSkuOption = useMemo((): SearchableSelectOption | null => {
    const trimmed = sku.trim();
    if (trimmed === "") return null;
    const match = skuOptions.find((option) => option.value === trimmed);
    return match ?? { value: trimmed, label: trimmed, description: null };
  }, [sku, skuOptions]);

  const unchanged = sku.trim().toLowerCase() === props.currentSku.trim().toLowerCase();

  return (
    <ExceptionModalShell open={props.open} title="Change SKU" onClose={props.onClose}>
      <p className="text-sm text-slate-600">
        Current SKU <span className="font-mono font-semibold text-slate-900">{props.currentSku}</span>. The label
        reprints after the change. If this SKU has a manufacturer part number, you scan it from the label next.
      </p>
      <div>
        <Label htmlFor="holding-correct-sku">SKU</Label>
        <SearchableSelect
          id="holding-correct-sku"
          presentation="inlineFilter"
          autoFocus
          value={sku}
          onValueChange={(option) => {
            setSku(option?.value ?? "");
            if (option != null) setSkuSearch(option.value);
          }}
          options={skuOptions}
          searchValue={skuSearch}
          onSearchChange={(query) => {
            setSkuSearch(query);
            if (query.trim() === "") setSku("");
          }}
          placeholder="Search catalog SKU"
          searchPlaceholder="Search catalog SKU"
          emptyMessage={skuSearchQuery.isFetching ? "Searching…" : "No matching SKUs"}
          isLoading={skuSearchQuery.isFetching}
          selectedLabelOverride={selectedSkuOption?.label ?? null}
        />
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" variant="secondary" onClick={props.onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          disabled={props.pending || sku.trim() === "" || unchanged}
          onClick={() => {
            const trimmedSku = sku.trim();
            if (trimmedSku === "" || trimmedSku.toLowerCase() === props.currentSku.trim().toLowerCase()) return;
            props.onSubmit(trimmedSku);
          }}
        >
          {props.pending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
          Update and print
        </Button>
      </div>
    </ExceptionModalShell>
  );
}
